/**
 * LION SPORT CLUB
 * Product Page Premium
 * Shopify Horizon 4.1.3
 */

(() => {
  "use strict";

  const SELECTORS = {
    stockBlock: "[data-lion-size-stock]",
    stockMessage: "[data-lion-stock]",
    stockText: "[data-lion-stock-text]",
    variantData: "[data-lion-variant-data]",
    sizeOpen: "[data-lion-size-open]",
    sizeClose: "[data-lion-size-close]",
    sizeDialog: "[data-lion-size-dialog]",
    deliveryBlock: "[data-lion-product-trust]",
    deliveryDate: "[data-lion-delivery-date]"
  };

  /**
   * Convierte un valor en número entero seguro.
   */
  const toInteger = (value, fallback = 0) => {
    const parsed = Number.parseInt(String(value), 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  /**
   * Obtiene el ámbito del selector de variantes.
   */
  const getProductScope = (element) => {
    return (
      element.closest(".product-details") ||
      element.closest(".shopify-section") ||
      document
    );
  };

  /**
   * Lee las variantes almacenadas en el bloque Liquid.
   */
  const getVariants = (block) => {
    const script = block.querySelector(SELECTORS.variantData);

    if (!script) return [];

    try {
      const variants = JSON.parse(script.textContent || "[]");
      return Array.isArray(variants) ? variants : [];
    } catch (error) {
      console.warn("LION: no se pudieron leer las variantes.", error);
      return [];
    }
  };

  /**
   * Lee las opciones seleccionadas en el selector nativo de Horizon.
   */
  const getSelectedOptions = (block) => {
    const scope = getProductScope(block);
    const picker = scope.querySelector("variant-picker");

    if (!picker) return [];

    const optionGroups = Array.from(
      picker.querySelectorAll(".variant-option")
    );

    return optionGroups
      .map((group) => {
        const checkedInput = group.querySelector(
          'input[type="radio"]:checked'
        );

        if (checkedInput instanceof HTMLInputElement) {
          return checkedInput.value;
        }

        const select = group.querySelector("select");

        if (select instanceof HTMLSelectElement) {
          return select.value;
        }

        return null;
      })
      .filter((value) => value !== null);
  };

  /**
   * Localiza la variante exacta correspondiente a las opciones.
   */
  const findSelectedVariant = (block, variants) => {
    const selectedOptions = getSelectedOptions(block);

    if (selectedOptions.length > 0) {
      const exactVariant = variants.find((variant) => {
        if (!Array.isArray(variant.options)) return false;
        if (variant.options.length !== selectedOptions.length) return false;

        return variant.options.every(
          (option, index) => String(option) === String(selectedOptions[index])
        );
      });

      if (exactVariant) return exactVariant;
    }

    const scope = getProductScope(block);
    const variantInput = scope.querySelector('input[name="id"]');

    if (variantInput instanceof HTMLInputElement && variantInput.value) {
      const variantById = variants.find(
        (variant) => String(variant.id) === String(variantInput.value)
      );

      if (variantById) return variantById;
    }

    return variants.find((variant) => variant.available) || variants[0] || null;
  };

  /**
   * Actualiza el mensaje de stock bajo.
   */
  const updateStock = (block) => {
    const message = block.querySelector(SELECTORS.stockMessage);
    const text = block.querySelector(SELECTORS.stockText);

    if (!(message instanceof HTMLElement) || !(text instanceof HTMLElement)) {
      return;
    }

    const variants = getVariants(block);
    const selectedVariant = findSelectedVariant(block, variants);
    const threshold = toInteger(block.dataset.lowStockThreshold, 5);

    if (!selectedVariant) {
      message.hidden = true;
      message.classList.add("is-hidden");
      text.textContent = "";
      return;
    }

    const quantity = Number(selectedVariant.inventory_quantity);
    const inventoryTracked = Boolean(
      selectedVariant.inventory_management
    );

    const continuesSelling =
      selectedVariant.inventory_policy === "continue";

    const shouldShow =
      selectedVariant.available === true &&
      inventoryTracked &&
      continuesSelling === false &&
      Number.isFinite(quantity) &&
      quantity > 0 &&
      quantity <= threshold;

    if (!shouldShow) {
      message.hidden = true;
      message.classList.add("is-hidden");
      text.textContent = "";
      return;
    }

    text.textContent =
      quantity === 1
        ? "Solo queda 1 unidad"
        : `Solo quedan ${quantity} unidades`;

    message.hidden = false;
    message.classList.remove("is-hidden");
  };

  /**
   * Inicializa stock y guía de tallas.
   */
  const initialiseSizeStockBlock = (block) => {
    if (!(block instanceof HTMLElement)) return;
    if (block.dataset.lionInitialised === "true") return;

    block.dataset.lionInitialised = "true";

    const scope = getProductScope(block);
    const openButton = block.querySelector(SELECTORS.sizeOpen);
    const closeButton = block.querySelector(SELECTORS.sizeClose);
    const dialog = block.querySelector(SELECTORS.sizeDialog);

    if (
      openButton instanceof HTMLButtonElement &&
      dialog instanceof HTMLDialogElement
    ) {
      openButton.addEventListener("click", () => {
        if (!dialog.open) {
          dialog.showModal();
          document.documentElement.classList.add(
            "lion-size-modal-open"
          );
        }
      });
    }

    if (
      closeButton instanceof HTMLButtonElement &&
      dialog instanceof HTMLDialogElement
    ) {
      closeButton.addEventListener("click", () => {
        dialog.close();
      });
    }

    if (dialog instanceof HTMLDialogElement) {
      dialog.addEventListener("click", (event) => {
        if (event.target === dialog) {
          dialog.close();
        }
      });

      dialog.addEventListener("close", () => {
        document.documentElement.classList.remove(
          "lion-size-modal-open"
        );
      });
    }

    scope.addEventListener(
      "change",
      () => {
        window.requestAnimationFrame(() => {
          updateStock(block);
        });

        window.setTimeout(() => {
          updateStock(block);
        }, 120);
      },
      true
    );

    updateStock(block);
  };

  /**
   * Añade días laborables, excluyendo sábado y domingo.
   */
  const addBusinessDays = (startingDate, amount) => {
    const date = new Date(
      startingDate.getFullYear(),
      startingDate.getMonth(),
      startingDate.getDate()
    );

    let addedDays = 0;

    while (addedDays < amount) {
      date.setDate(date.getDate() + 1);

      const dayOfWeek = date.getDay();
      const isBusinessDay = dayOfWeek !== 0 && dayOfWeek !== 6;

      if (isBusinessDay) {
        addedDays += 1;
      }
    }

    return date;
  };

  /**
   * Formatea una fecha según el idioma activo de Shopify.
   */
  const formatDeliveryDate = (date) => {
    const locale = document.documentElement.lang || "es-ES";

    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short"
    })
      .format(date)
      .replace(".", "");
  };

  /**
   * Calcula el intervalo de entrega.
   */
  const initialiseDeliveryBlock = (block) => {
    if (!(block instanceof HTMLElement)) return;
    if (block.dataset.lionDeliveryInitialised === "true") return;

    block.dataset.lionDeliveryInitialised = "true";

    const target = block.querySelector(SELECTORS.deliveryDate);

    if (!(target instanceof HTMLElement)) return;

    let minimumDays = toInteger(block.dataset.deliveryMin, 2);
    let maximumDays = toInteger(block.dataset.deliveryMax, 4);

    minimumDays = Math.max(1, minimumDays);
    maximumDays = Math.max(1, maximumDays);

    if (minimumDays > maximumDays) {
      const temporaryValue = minimumDays;
      minimumDays = maximumDays;
      maximumDays = temporaryValue;
    }

    const today = new Date();
    const minimumDate = addBusinessDays(today, minimumDays);
    const maximumDate = addBusinessDays(today, maximumDays);

    target.textContent =
      `${formatDeliveryDate(minimumDate)} – ` +
      `${formatDeliveryDate(maximumDate)}`;
  };

  /**
   * Inicializa todos los bloques nuevos.
   */
  const initialiseLionProduct = (root = document) => {
    root
      .querySelectorAll(SELECTORS.stockBlock)
      .forEach(initialiseSizeStockBlock);

    root
      .querySelectorAll(SELECTORS.deliveryBlock)
      .forEach(initialiseDeliveryBlock);
  };

  /**
   * Detecta bloques cargados o sustituidos dinámicamente.
   */
  const observeDynamicContent = () => {
    if (!document.body) return;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;

          if (
            node.matches(SELECTORS.stockBlock) ||
            node.matches(SELECTORS.deliveryBlock)
          ) {
            initialiseLionProduct(node.parentElement || document);
            continue;
          }

          if (
            node.querySelector(SELECTORS.stockBlock) ||
            node.querySelector(SELECTORS.deliveryBlock)
          ) {
            initialiseLionProduct(node);
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  };

  const boot = () => {
    initialiseLionProduct(document);
    observeDynamicContent();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, {
      once: true
    });
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target instanceof HTMLElement) {
      initialiseLionProduct(event.target);
    }
  });

  document.addEventListener("shopify:block:select", (event) => {
    if (event.target instanceof HTMLElement) {
      initialiseLionProduct(event.target);
    }
  });
})();